export interface ServiceWorkerConfig {
  onSuccess?: (registration: ServiceWorkerRegistration) => void
  onUpdate?: (registration: ServiceWorkerRegistration) => void
  onOfflineReady?: () => void
}

export async function registerServiceWorker(
  config?: ServiceWorkerConfig
): Promise<ServiceWorkerRegistration | undefined> {
  // Check if service workers are supported
  if (!('serviceWorker' in navigator)) {
    console.log('[SW] Service workers not supported in this browser')
    return undefined
  }

  // Only register in production or when explicitly enabled
  if (import.meta.env.DEV && !import.meta.env.VITE_SW_DEV) {
    console.log('[SW] Service worker disabled in development')
    return undefined
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
    })

    console.log('[SW] Registered successfully:', registration.scope)

    // Check for updates every hour
    setInterval(
      () => {
        console.log('[SW] Checking for updates...')
        registration.update().catch(error => {
          console.error('[SW] Update check failed:', error)
        })
      },
      60 * 60 * 1000
    )

    // Handle service worker updates
    registration.addEventListener('updatefound', () => {
      const newWorker = registration.installing

      if (!newWorker) return

      console.log('[SW] Update found, installing new version...')

      newWorker.addEventListener('statechange', () => {
        console.log('[SW] New worker state:', newWorker.state)

        if (
          newWorker.state === 'installed' &&
          navigator.serviceWorker.controller
        ) {
          // New service worker available
          console.log('[SW] New version installed and waiting')
          config?.onUpdate?.(registration)
        } else if (newWorker.state === 'activated') {
          // Service worker activated (first install)
          console.log('[SW] Service worker activated')
          config?.onSuccess?.(registration)
          config?.onOfflineReady?.()
        }
      })
    })

    return registration
  } catch (error) {
    console.error('[SW] Registration failed:', error)
    // NO fallback - application continues without service worker
    throw error
  }
}

export async function unregisterServiceWorker(): Promise<void> {
  if (!('serviceWorker' in navigator)) {
    return
  }

  try {
    const registration = await navigator.serviceWorker.getRegistration()

    if (registration) {
      const unregistered = await registration.unregister()
      if (unregistered) {
        console.log('[SW] Unregistered successfully')
      }
    }
  } catch (error) {
    console.error('[SW] Unregistration failed:', error)
    throw error
  }
}
